// The online transport: a Colyseus room on our server. Quick-join the public table, join a friend's code, or open a
// new private table. Holds the reconnection token and gets the same seat back after a blip (the server keeps it for
// RECONNECT_SEC and a bot keeps the body looking normal meanwhile).
import { Client, type Room } from "colyseus.js";
import { ROOM_NAME, PROTOCOL_VERSION, type JoinOptions, type InputMsg, type Snap, type SeatMsg, type WireEvent, type PopMsg, type MarkMsg, type WhistleMsg } from "../../../shared/protocol";
import type { Cosmetics } from "../../../shared/sim";
import type { Transport, TransportHandlers } from "./transport";
import type { Target } from "./types";

const STALE_MS = 3500;   // no snapshot for this long while "connected": the radio went quiet — reconnect

export class OnlineTransport implements Transport {
  readonly kind = "online" as const;
  private room: Room | null = null; private token = ""; private leaving = false; private reconnecting = false;
  private lastSnapAt = 0;
  private client: Client; private http: string;

  constructor(endpoint: string, private h: TransportHandlers) {
    this.client = new Client(endpoint);
    this.http = endpoint.replace(/^ws/, "http").replace(/\/$/, "");
  }

  /** join; resolves false (with a reason) instead of throwing */
  async open(target: Target, join: JoinOptions): Promise<{ ok: boolean; error?: string; code?: string }> {
    const opts: JoinOptions = { ...join, v: PROTOCOL_VERSION };
    try {
      let room: Room;
      if (target.kind === "public") room = await this.client.joinOrCreate(ROOM_NAME, { ...opts, public: true });
      else {
        let roomId: string, code = target.kind === "code" ? target.code.toUpperCase().trim() : "";
        if (target.kind === "new") {
          const r = await fetch(`${this.http}/r`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
          if (!r.ok) throw new Error("could not open a table");
          const j = await r.json(); roomId = j.roomId; code = j.code;
        } else {
          const r = await fetch(`${this.http}/r/${encodeURIComponent(code)}`);
          if (!r.ok) throw new Error(r.status === 404 ? "no table with that code" : "table lookup failed");
          roomId = (await r.json()).roomId;
        }
        room = await this.client.joinById(roomId, opts);
      }
      this.attach(room);
      return { ok: true };
    } catch (e: unknown) { return { ok: false, error: (e as Error)?.message || String(e) }; }
  }

  private attach(room: Room) {
    this.room = room; this.token = room.reconnectionToken; this.leaving = false; this.lastSnapAt = performance.now();
    room.onMessage("seat", (m: SeatMsg) => this.h.seat(m));
    room.onMessage("snap", (s: Snap) => { this.lastSnapAt = performance.now(); this.h.snap(s, this.lastSnapAt); });
    room.onMessage("ev", (evs: WireEvent[]) => this.h.events(evs));
    room.onLeave(() => { this.room = null; if (!this.leaving && this.token) this.reconnect(); });
    room.onError((code, msg) => this.h.status("error", `${code}: ${msg}`));
  }

  private async reconnect() {
    if (this.reconnecting) return;
    this.reconnecting = true; this.h.status("reconnecting");
    for (const wait of [300, 800, 1500, 2500, 3500, 4500]) {   // ~13 s, inside the server's 15 s hold
      await new Promise((r) => setTimeout(r, wait));
      if (this.leaving) { this.reconnecting = false; return; }
      try {
        const room = await this.client.reconnect(this.token);
        this.attach(room); this.reconnecting = false; this.h.status("online"); return;
      } catch { /* try again */ }
    }
    this.reconnecting = false; this.h.status("lost");
  }

  pump(nowMs: number) {
    // snapshots stopped without the socket saying so (a phone's radio went quiet): drop it and reconnect
    if (!this.room || this.reconnecting || nowMs - this.lastSnapAt < STALE_MS) return;
    const r = this.room; this.room = null;
    try { r.removeAllListeners(); r.connection.close(); } catch { /* already closed */ }
    this.reconnect();
  }

  input(m: InputMsg) { this.room?.send("i", m); }
  pop(m: PopMsg) { this.room?.send("pop", m); }
  mark(m: MarkMsg) { this.room?.send("mark", m); }
  whistle(m: WhistleMsg) { this.room?.send("whistle", m); }
  cos(c: Partial<Cosmetics>) { this.room?.send("cos", c); }
  name(n: string) { this.room?.send("name", n); }
  close() { this.leaving = true; this.room?.leave(); this.room = null; }
}
